import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Trash2, Edit2, Check, X, Upload, Download, LogOut, Home, Users, QrCode, Printer, FileDown, BarChart3 } from 'lucide-react';
import CountryManager from './CountryManager';
import CRMDashboard from './CRMDashboard';
import AnalyticsDashboard from './AnalyticsDashboard';
import { QRCodeSVG } from 'qrcode.react';
import jsPDF from 'jspdf';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminPanel = () => {
  const navigate = useNavigate();
  const [joinLinks, setJoinLinks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingData, setEditingData] = useState({});
  const [uploadingFile, setUploadingFile] = useState(false);
  const [activeTab, setActiveTab] = useState('links');
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [countryFilter, setCountryFilter] = useState('all');
  const [editingCountries, setEditingCountries] = useState([]);
  const [sortColumn, setSortColumn] = useState('priority');
  const [sortDirection, setSortDirection] = useState('asc');
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    join_now_link: '',
    seven_k_login_user: '',
    country: '',
    link_to_be_used: null,
    priority: 5
  });
  
  const [availableCountries, setAvailableCountries] = useState([]);
  
  const [selectedCountries, setSelectedCountries] = useState([]);
  
  // QR Code Modal State
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [qrModalData, setQrModalData] = useState({ name: '', link: '' });
  const qrPrintRef = useRef(null);

  // People who should have QR codes displayed
  const qrEnabledNames = ['gina', 'stuart', 'rominvest', 'alyssa', 'veronica', 'wout', 'harpreet', 'jonas', 'tara', 'angela'];

  // Print QR Code
  const handlePrintQR = () => {
    const printContent = qrPrintRef.current;
    if (!printContent) return;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>QR Code - ${qrModalData.name}</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;
              min-height: 100vh;
              margin: 0;
              padding: 20px;
              box-sizing: border-box;
            }
            .print-container {
              text-align: center;
              padding: 40px;
              border: 2px solid #D4AF37;
              border-radius: 16px;
              max-width: 400px;
            }
            .print-title {
              color: #D4AF37;
              font-size: 24px;
              font-weight: bold;
              margin-bottom: 20px;
            }
            .qr-container {
              background: white;
              padding: 20px;
              border-radius: 12px;
              display: inline-block;
              margin-bottom: 20px;
            }
            .print-link {
              color: #333;
              font-size: 12px;
              word-break: break-all;
              margin-top: 15px;
              padding: 10px;
              background: #f5f5f5;
              border-radius: 6px;
            }
            .print-footer {
              margin-top: 20px;
              color: #666;
              font-size: 14px;
            }
            @media print {
              body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            }
          </style>
        </head>
        <body>
          <div class="print-container">
            <div class="print-title">${qrModalData.name}</div>
            <div class="qr-container">
              ${printContent.querySelector('.qr-modal-code').innerHTML}
            </div>
            <div class="print-link">${qrModalData.link}</div>
            <div class="print-footer">Scan to Sign Up</div>
          </div>
          <script>
            window.onload = function() {
              window.print();
              window.onafterprint = function() { window.close(); };
            };
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  // Save QR Code as PDF
  const handleSaveAsPDF = async () => {
    try {
      toast.info('Generating PDF...');
      
      // Get the SVG element from the modal
      const svgElement = qrPrintRef.current?.querySelector('.qr-modal-code svg');
      if (!svgElement) {
        toast.error('QR code not found');
        return;
      }

      // Convert SVG to data URL
      const svgData = new XMLSerializer().serializeToString(svgElement);
      const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
      const svgUrl = URL.createObjectURL(svgBlob);

      // Create an image from the SVG
      const img = new Image();
      img.onload = () => {
        // Create canvas and draw the image
        const canvas = document.createElement('canvas');
        const size = 300;
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = 'white';
        ctx.fillRect(0, 0, size, size);
        ctx.drawImage(img, 0, 0, size, size);

        // Create PDF
        const pdf = new jsPDF({
          orientation: 'portrait',
          unit: 'mm',
          format: 'a4'
        });

        const pageWidth = pdf.internal.pageSize.getWidth();
        const qrSize = 80; // QR code size in mm
        const xPos = (pageWidth - qrSize) / 2;

        // Add title in black
        pdf.setFontSize(24);
        pdf.setTextColor(0, 0, 0); // Black color
        pdf.text(qrModalData.name, pageWidth / 2, 40, { align: 'center' });

        // Add QR code image
        const imgData = canvas.toDataURL('image/png');
        pdf.addImage(imgData, 'PNG', xPos, 55, qrSize, qrSize);

        // Add footer
        pdf.setFontSize(12);
        pdf.setTextColor(80, 80, 80);
        pdf.text('Scan to Sign Up', pageWidth / 2, 145, { align: 'center' });

        // Save the PDF
        const filename = `QR_${qrModalData.name.replace(/\s+/g, '_')}.pdf`;
        pdf.save(filename);

        // Clean up
        URL.revokeObjectURL(svgUrl);
        toast.success('PDF saved successfully!');
      };

      img.onerror = () => {
        toast.error('Failed to generate QR image');
        URL.revokeObjectURL(svgUrl);
      };

      img.src = svgUrl;
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Failed to generate PDF');
    }
  };

  useEffect(() => {
    fetchJoinLinks();
    fetchCountries();
  }, []);

  const fetchCountries = async () => {
    try {
      const response = await axios.get(`${API}/countries/active`);
      setAvailableCountries(response.data.map(c => c.name));
    } catch (error) {
      console.error('Error fetching countries:', error);
      toast.error('Failed to load countries');
    }
  };

  const fetchJoinLinks = async () => {
    try {
      const response = await axios.get(`${API}/join-links/`);
      setJoinLinks(response.data);
    } catch (error) {
      console.error('Error fetching join links:', error);
      toast.error('Failed to fetch join links');
    }
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      // Toggle direction if same column
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new column with ascending order
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const getFilteredLinks = () => {
    let filtered = joinLinks;
    
    // Apply country filter
    if (countryFilter !== 'all') {
      filtered = filtered.filter(link => 
        link.country.toLowerCase() === countryFilter.toLowerCase()
      );
    }
    
    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(link => {
        return (
          link.first_name.toLowerCase().includes(query) ||
          link.last_name.toLowerCase().includes(query) ||
          link.join_now_link.toLowerCase().includes(query) ||
          link.seven_k_login_user.toLowerCase().includes(query) ||
          link.country.toLowerCase().includes(query) ||
          (link.link_to_be_used && link.link_to_be_used.toLowerCase().includes(query)) ||
          link.uses_left.toString().includes(query)
        );
      });
    }
    
    // Apply sorting
    filtered = [...filtered].sort((a, b) => {
      let aValue = a[sortColumn];
      let bValue = b[sortColumn];
      
      // Handle null/undefined values
      if (aValue === null || aValue === undefined) aValue = '';
      if (bValue === null || bValue === undefined) bValue = '';
      
      // Handle numeric columns
      if (sortColumn === 'priority' || sortColumn === 'uses_left') {
        aValue = Number(aValue) || 0;
        bValue = Number(bValue) || 0;
        
        // Special handling for priority: 0 should be treated as lowest priority
        if (sortColumn === 'priority') {
          // Treat 0 as infinity for sorting purposes
          if (aValue === 0) aValue = Infinity;
          if (bValue === 0) bValue = Infinity;
        }
      } else {
        // Convert to string for text comparison
        aValue = String(aValue).toLowerCase();
        bValue = String(bValue).toLowerCase();
      }
      
      if (sortDirection === 'asc') {
        return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
      } else {
        return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
      }
    });
    
    return filtered;
  };

  // Get unique countries for filter dropdown (from all links)
  const getUniqueCountries = () => {
    const countries = new Set();
    
    joinLinks.forEach(link => {
      if (link.country) {
        countries.add(link.country);
      }
    });
    return Array.from(countries).sort();
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCountryToggle = (country) => {
    setSelectedCountries(prev => {
      if (prev.includes(country)) {
        return prev.filter(c => c !== country);
      } else {
        return [...prev, country];
      }
    });
  };

  const handleSelectAllCountries = () => {
    if (selectedCountries.length === availableCountries.length) {
      setSelectedCountries([]);
    } else {
      setSelectedCountries([...availableCountries]);
    }
  };

  const getCountryDisplayValue = () => {
    if (selectedCountries.length === 0) return '';
    if (selectedCountries.length === availableCountries.length) return 'All';
    return selectedCountries.join(', ');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (selectedCountries.length === 0) {
      toast.error('Please select at least one country');
      return;
    }
    
    setLoading(true);

    try {
      // Always send actual country names (never "All")
      const countryValue = selectedCountries.join(',');
      
      await axios.post(`${API}/join-links/`, {
        ...formData,
        country: countryValue
      });
      toast.success('Join link created successfully');
      setFormData({
        first_name: '',
        last_name: '',
        join_now_link: '',
        seven_k_login_user: '',
        country: '',
        link_to_be_used: null,
        priority: 5
      });
      setSelectedCountries([]);
      fetchJoinLinks();
    } catch (error) {
      console.error('Error creating join link:', error);
      toast.error('Failed to create join link');
    } finally {
      setLoading(false);
    }
  };

  const handleLinkStatusChange = async (id, newStatus) => {
    console.log('Toggle clicked:', { id, newStatus });
    
    try {
      const payload = { link_to_be_used: newStatus === 'yes' ? 'yes' : 'no' };
      
      // If setting to "No", also set priority to 0
      if (newStatus === 'no') {
        payload.priority = 0;
      }
      
      console.log('Sending PATCH:', payload);
      
      const response = await axios.patch(`${API}/join-links/${id}`, payload);
      console.log('PATCH response:', response.data);
      
      // Update the state immediately with the response
      setJoinLinks(prevLinks => 
        prevLinks.map(link => {
          if (link.id === id) {
            console.log('Updating link in state:', { old: link.link_to_be_used, new: response.data.link_to_be_used });
            return { 
              ...link, 
              link_to_be_used: response.data.link_to_be_used,
              priority: response.data.priority
            };
          }
          return link;
        })
      );
      
      const message = newStatus === 'yes' 
        ? 'Link activated' 
        : 'Link deactivated and priority set to 0';
      toast.success(message);
    } catch (error) {
      console.error('Error updating join link:', error);
      toast.error('Failed to update join link');
      fetchJoinLinks(); // Refresh on error
    }
  };

  const handleUsesLeftChange = async (id, newValue) => {
    const usesLeft = parseInt(newValue);
    
    if (isNaN(usesLeft)) {
      toast.error('Please enter a valid number');
      fetchJoinLinks(); // Refresh to reset the invalid value
      return;
    }
    
    if (usesLeft < 0) {
      toast.error('Uses left cannot be negative (minimum: 0)');
      fetchJoinLinks(); // Refresh to reset the invalid value
      return;
    }

    try {
      await axios.patch(`${API}/join-links/${id}`, {
        uses_left: usesLeft
      });
      toast.success('Uses left updated successfully');
      fetchJoinLinks();
    } catch (error) {
      console.error('Error updating uses left:', error);
      toast.error('Failed to update uses left');
    }
  };

  const handlePriorityChange = async (id, newValue) => {
    const priority = parseInt(newValue);
    
    if (isNaN(priority) || priority < 0 || priority > 10) {
      toast.error('Priority must be between 0 and 10');
      fetchJoinLinks(); // Refresh to reset the invalid value
      return;
    }

    try {
      await axios.patch(`${API}/join-links/${id}`, {
        priority: priority
      });
      toast.success('Priority updated successfully');
      fetchJoinLinks();
    } catch (error) {
      console.error('Error updating priority:', error);
      toast.error('Failed to update priority');
    }
  };

  const handleEdit = (link) => {
    setEditingId(link.id);
    setEditingData({
      first_name: link.first_name,
      last_name: link.last_name,
      join_now_link: link.join_now_link,
      seven_k_login_user: link.seven_k_login_user,
      country: link.country,
      link_to_be_used: link.link_to_be_used,
      uses_left: link.uses_left,
      priority: link.priority || 5
    });
    
    // Set editing countries based on current country value
    if (link.country === 'All') {
      setEditingCountries([...availableCountries]);
    } else if (link.country) {
      setEditingCountries(link.country.split(',').map(c => c.trim()));
    } else {
      setEditingCountries([]);
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditingData({});
    setEditingCountries([]);
  };

  const handleEditInputChange = (field, value) => {
    const updates = { [field]: value };
    
    // If link_to_be_used is set to 'no', automatically set priority to 0
    if (field === 'link_to_be_used' && value === 'no') {
      updates.priority = 0;
    }
    
    setEditingData(prev => ({ ...prev, ...updates }));
  };

  const handleSaveEdit = async (id) => {
    if (editingCountries.length === 0) {
      toast.error('Please select at least one country');
      return;
    }
    
    try {
      // Always send actual country names (never "All")
      const countryValue = editingCountries.join(',');
      
      const updatePayload = {
        ...editingData,
        country: countryValue
      };
      
      // If link_to_be_used is set to 'no', automatically set priority to 0
      if (editingData.link_to_be_used === 'no') {
        updatePayload.priority = 0;
      }
      
      await axios.patch(`${API}/join-links/${id}`, updatePayload);
      toast.success('Link updated successfully');
      setEditingId(null);
      setEditingData({});
      setEditingCountries([]);
      fetchJoinLinks();
    } catch (error) {
      console.error('Error updating join link:', error);
      toast.error('Failed to update join link');
    }
  };

  const handleEditCountryToggle = (country) => {
    setEditingCountries(prev => {
      if (prev.includes(country)) {
        return prev.filter(c => c !== country);
      } else {
        return [...prev, country];
      }
    });
  };

  const handleEditSelectAllCountries = () => {
    if (editingCountries.length === availableCountries.length) {
      setEditingCountries([]);
    } else {
      setEditingCountries([...availableCountries]);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this link?')) {
      return;
    }

    try {
      await axios.delete(`${API}/join-links/${id}`);
      toast.success('Link deleted successfully');
      fetchJoinLinks();
    } catch (error) {
      console.error('Error deleting join link:', error);
      toast.error('Failed to delete join link');
    }
  };

  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.target.style.opacity = '0.5';
  };

  const handleDragEnd = (e) => {
    e.target.style.opacity = '1';
    setDraggedIndex(null);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, dropIndex) => {
    e.preventDefault();
    
    if (draggedIndex === null || draggedIndex === dropIndex) {
      return;
    }

    const filteredLinks = getFilteredLinks();
    const newLinks = [...filteredLinks];
    const draggedItem = newLinks[draggedIndex];
    
    // Remove from old position
    newLinks.splice(draggedIndex, 1);
    // Insert at new position
    newLinks.splice(dropIndex, 0, draggedItem);
    
    // Update display_order for all affected items
    const reorderData = newLinks.map((link, index) => ({
      id: link.id,
      display_order: index
    }));

    try {
      await axios.post(`${API}/join-links/reorder`, reorderData);
      toast.success('Links reordered successfully');
      fetchJoinLinks();
    } catch (error) {
      console.error('Error reordering links:', error);
      toast.error('Failed to reorder links');
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      toast.error('Please upload an Excel file (.xlsx or .xls)');
      return;
    }

    setUploadingFile(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/join-links/bulk-upload/`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      toast.success(response.data.message);
      if (response.data.errors && response.data.errors.length > 0) {
        console.error('Upload errors:', response.data.errors);
        toast.warning(`${response.data.errors.length} rows had errors. Check console for details.`);
      }
      fetchJoinLinks();
      e.target.value = ''; // Reset file input
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload file');
    } finally {
      setUploadingFile(false);
    }
  };

  const downloadTemplate = () => {
    // Create a sample Excel template
    const csvContent = 'first_name,last_name,join_now_link,seven_k_login_user,country,link_to_be_used\nJohn,Doe,https://example.com/join,johndoe@example.com,Romania,yes\nJane,Smith,https://example.com/join2,janesmith@example.com,Romania,';
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'join_links_template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const handleLogout = () => {
    localStorage.removeItem('adminAuthenticated');
    localStorage.removeItem('adminLoginTime');
    toast.success('Logged out successfully');
    navigate('/admin/login');
  };

  return (
    <div className="admin-panel">
      <div className="admin-container">
        <div className="admin-header-section">
          <h1 className="admin-title">Join Now Links Admin Panel</h1>
          <div className="admin-header-buttons">
            <Button onClick={() => navigate('/')} variant="outline" className="home-btn">
              <Home size={16} />
              Home
            </Button>
            <Button onClick={handleLogout} variant="outline" className="logout-btn">
              <LogOut size={16} />
              Logout
            </Button>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex gap-2 mb-6">
          <Button
            onClick={() => setActiveTab('links')}
            className={activeTab === 'links' ? 'bg-yellow-600' : 'bg-gray-700'}
          >
            Manage Links
          </Button>
          <Button
            onClick={() => setActiveTab('countries')}
            className={activeTab === 'countries' ? 'bg-yellow-600' : 'bg-gray-700'}
          >
            Manage Countries
          </Button>
          <Button
            onClick={() => setActiveTab('crm')}
            className={activeTab === 'crm' ? 'bg-yellow-600' : 'bg-gray-700'}
          >
            <Users className="w-4 h-4 mr-2" />
            CRM
          </Button>
          <Button
            onClick={() => setActiveTab('analytics')}
            className={activeTab === 'analytics' ? 'bg-yellow-600' : 'bg-gray-700'}
          >
            <BarChart3 className="w-4 h-4 mr-2" />
            Analytics
          </Button>
        </div>

        {/* Country Management Tab */}
        {activeTab === 'countries' && <CountryManager />}

        {/* CRM Tab */}
        {activeTab === 'crm' && <CRMDashboard />}

        {/* Analytics Tab */}
        {activeTab === 'analytics' && <AnalyticsDashboard />}

        {/* Links Management Tab */}
        {activeTab === 'links' && (
        <>
        {/* Create Form */}
        <Card className="admin-card">
          <CardHeader>
            <CardTitle>Add New Join Link</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="admin-form">
              <div className="form-grid">
                <Input
                  type="text"
                  name="first_name"
                  placeholder="First Name"
                  value={formData.first_name}
                  onChange={handleInputChange}
                  required
                  className="admin-input"
                />
                <Input
                  type="text"
                  name="last_name"
                  placeholder="Last Name"
                  value={formData.last_name}
                  onChange={handleInputChange}
                  required
                  className="admin-input"
                />
                <Input
                  type="url"
                  name="join_now_link"
                  placeholder="Join Now Link"
                  value={formData.join_now_link}
                  onChange={handleInputChange}
                  required
                  className="admin-input"
                />
                <Input
                  type="text"
                  name="seven_k_login_user"
                  placeholder="7K Login User"
                  value={formData.seven_k_login_user}
                  onChange={handleInputChange}
                  required
                  className="admin-input"
                />
                <div className="country-multiselect-container">
                  <label className="select-label">Country (Select one or more):</label>
                  <div className="country-checkboxes">
                    <div className="country-checkbox-item">
                      <input
                        type="checkbox"
                        id="all-countries"
                        checked={selectedCountries.length === availableCountries.length}
                        onChange={handleSelectAllCountries}
                        className="country-checkbox"
                      />
                      <label htmlFor="all-countries" className="country-checkbox-label">
                        <strong>All Countries</strong>
                      </label>
                    </div>
                    {availableCountries.map(country => (
                      <div key={country} className="country-checkbox-item">
                        <input
                          type="checkbox"
                          id={`country-${country}`}
                          checked={selectedCountries.includes(country)}
                          onChange={() => handleCountryToggle(country)}
                          className="country-checkbox"
                        />
                        <label htmlFor={`country-${country}`} className="country-checkbox-label">
                          {country}
                        </label>
                      </div>
                    ))}
                  </div>
                  {selectedCountries.length > 0 && (
                    <div className="selected-countries-display">
                      Selected: {getCountryDisplayValue()}
                    </div>
                  )}
                </div>
                <div className="select-container">
                  <label htmlFor="priority" className="select-label">Priority (0-10):</label>
                  <select
                    id="priority"
                    name="priority"
                    value={formData.priority}
                    onChange={handleInputChange}
                    className="priority-select"
                  >
                    {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(num => (
                      <option key={num} value={num}>{num}</option>
                    ))}
                  </select>
                </div>
                <div className="checkbox-container">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.link_to_be_used === 'yes'}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        link_to_be_used: e.target.checked ? 'yes' : null
                      }))}
                      className="checkbox-input"
                    />
                    Set as Active Link
                  </label>
                </div>
              </div>
              <Button type="submit" disabled={loading} className="submit-btn">
                {loading ? 'Creating...' : 'Add Join Link'}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Bulk Upload Section */}
        <Card className="admin-card">
          <CardHeader>
            <CardTitle>Bulk Upload from Excel</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="upload-section">
              <div className="upload-info">
                <p className="upload-description">
                  Upload an Excel file (.xlsx or .xls) with the following columns:
                  <br />
                  <strong>first_name, last_name, join_now_link, seven_k_login_user, country, link_to_be_used</strong>
                </p>
                <Button
                  variant="outline"
                  onClick={downloadTemplate}
                  className="template-btn"
                >
                  <Download size={16} />
                  Download Template
                </Button>
              </div>
              <div className="file-upload-container">
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleFileUpload}
                  disabled={uploadingFile}
                  className="file-input"
                  id="excel-upload"
                />
                <label htmlFor="excel-upload" className="file-upload-label">
                  <Upload size={20} />
                  {uploadingFile ? 'Uploading...' : 'Choose Excel File'}
                </label>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Links Table */}
        <Card className="admin-card">
          <CardHeader>
            <CardTitle>Manage Join Links</CardTitle>
          </CardHeader>
          <CardContent>
            {/* Filter and Search Bar */}
            <div className="filter-search-container">
              <div className="filter-section">
                <label htmlFor="country-filter" className="filter-label">Country:</label>
                <select
                  id="country-filter"
                  value={countryFilter}
                  onChange={(e) => setCountryFilter(e.target.value)}
                  className="country-filter-select"
                >
                  <option value="all">All Countries</option>
                  {getUniqueCountries().map(country => (
                    <option key={country} value={country}>{country}</option>
                  ))}
                </select>
              </div>
              
              <div className="search-section">
                <Input
                  type="text"
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="search-input-compact"
                />
                {searchQuery && (
                  <button 
                    onClick={() => setSearchQuery('')}
                    className="search-clear-btn"
                    aria-label="Clear search"
                  >
                    <X size={16} />
                  </button>
                )}
              </div>
            </div>
            
            <div className="table-container">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th className="drag-handle-header"></th>
                    <th className="sortable-header" onClick={() => handleSort('first_name')}>
                      First Name {sortColumn === 'first_name' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </th>
                    <th className="sortable-header" onClick={() => handleSort('last_name')}>
                      Last Name {sortColumn === 'last_name' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </th>
                    <th className="sortable-header" onClick={() => handleSort('join_now_link')}>
                      Join Now Link {sortColumn === 'join_now_link' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </th>
                    <th className="sortable-header" onClick={() => handleSort('seven_k_login_user')}>
                      7K Login User {sortColumn === 'seven_k_login_user' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </th>
                    <th className="sortable-header" onClick={() => handleSort('country')}>
                      Country {sortColumn === 'country' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </th>
                    <th className="sortable-header" onClick={() => handleSort('priority')}>
                      Priority {sortColumn === 'priority' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </th>
                    <th className="sortable-header" onClick={() => handleSort('link_to_be_used')}>
                      Link to be Used {sortColumn === 'link_to_be_used' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </th>
                    <th>QR Code</th>
                    <th className="sortable-header" onClick={() => handleSort('uses_left')}>
                      Link Uses Left {sortColumn === 'uses_left' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {getFilteredLinks().length === 0 ? (
                    <tr>
                      <td colSpan="11" className="no-data">No join links found</td>
                    </tr>
                  ) : (
                    getFilteredLinks().map((link, index) => (
                      <tr 
                        key={link.id} 
                        className={link.link_to_be_used === 'yes' ? 'active-row' : ''}
                        draggable={editingId === null}
                        onDragStart={(e) => handleDragStart(e, index)}
                        onDragEnd={handleDragEnd}
                        onDragOver={handleDragOver}
                        onDrop={(e) => handleDrop(e, index)}
                      >
                        {/* Drag Handle */}
                        <td className="drag-handle-cell">
                          <span className="drag-handle">⋮⋮</span>
                        </td>
                        
                        {/* First Name */}
                        <td>
                          {editingId === link.id ? (
                            <Input
                              value={editingData.first_name}
                              onChange={(e) => handleEditInputChange('first_name', e.target.value)}
                              className="edit-input"
                            />
                          ) : (
                            link.first_name
                          )}
                        </td>
                        
                        {/* Last Name */}
                        <td>
                          {editingId === link.id ? (
                            <Input
                              value={editingData.last_name}
                              onChange={(e) => handleEditInputChange('last_name', e.target.value)}
                              className="edit-input"
                            />
                          ) : (
                            link.last_name
                          )}
                        </td>
                        
                        {/* Join Now Link */}
                        <td className="link-cell">
                          {editingId === link.id ? (
                            <Input
                              value={editingData.join_now_link}
                              onChange={(e) => handleEditInputChange('join_now_link', e.target.value)}
                              className="edit-input"
                            />
                          ) : (
                            <a href={link.join_now_link} target="_blank" rel="noopener noreferrer">
                              {link.join_now_link.substring(0, 40)}...
                            </a>
                          )}
                        </td>
                        
                        {/* 7K Login User */}
                        <td>
                          {editingId === link.id ? (
                            <Input
                              value={editingData.seven_k_login_user}
                              onChange={(e) => handleEditInputChange('seven_k_login_user', e.target.value)}
                              className="edit-input"
                            />
                          ) : (
                            link.seven_k_login_user
                          )}
                        </td>
                        
                        {/* Country */}
                        <td className="country-cell">
                          {editingId === link.id ? (
                            <div className="country-edit-checkboxes">
                              <div className="country-checkbox-item-inline">
                                <input
                                  type="checkbox"
                                  id={`edit-all-${link.id}`}
                                  checked={editingCountries.length === availableCountries.length}
                                  onChange={handleEditSelectAllCountries}
                                  className="country-checkbox-inline"
                                />
                                <label htmlFor={`edit-all-${link.id}`} className="country-checkbox-label-inline">
                                  All
                                </label>
                              </div>
                              {availableCountries.map(country => (
                                <div key={country} className="country-checkbox-item-inline">
                                  <input
                                    type="checkbox"
                                    id={`edit-${country}-${link.id}`}
                                    checked={editingCountries.includes(country)}
                                    onChange={() => handleEditCountryToggle(country)}
                                    className="country-checkbox-inline"
                                  />
                                  <label htmlFor={`edit-${country}-${link.id}`} className="country-checkbox-label-inline">
                                    {country}
                                  </label>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <span className={link.country === 'All' ? 'country-all-badge' : 'country-badge'}>
                              {link.country}
                            </span>
                          )}
                        </td>
                        
                        {/* Priority */}
                        <td className="priority-cell">
                          {editingId === link.id ? (
                            <select
                              value={editingData.priority !== undefined ? editingData.priority : 5}
                              onChange={(e) => handleEditInputChange('priority', parseInt(e.target.value))}
                              className="priority-select-table"
                            >
                              {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(num => (
                                <option key={num} value={num}>{num}</option>
                              ))}
                            </select>
                          ) : (
                            <select
                              value={link.priority !== undefined ? link.priority : 5}
                              onChange={(e) => handlePriorityChange(link.id, e.target.value)}
                              className="priority-select-table"
                            >
                              {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(num => (
                                <option key={num} value={num}>{num}</option>
                              ))}
                            </select>
                          )}
                        </td>
                        
                        {/* Link to be Used */}
                        <td>
                          {editingId === link.id ? (
                            <select
                              value={editingData.link_to_be_used === 'yes' ? 'yes' : 'no'}
                              onChange={(e) => handleEditInputChange('link_to_be_used', e.target.value)}
                              className="status-select"
                            >
                              <option value="no">No</option>
                              <option value="yes">Yes</option>
                            </select>
                          ) : (
                            <select
                              value={link.link_to_be_used === 'yes' ? 'yes' : 'no'}
                              onChange={(e) => handleLinkStatusChange(link.id, e.target.value)}
                              className="status-select"
                            >
                              <option value="no">No</option>
                              <option value="yes">Yes</option>
                            </select>
                          )}
                        </td>
                        
                        {/* QR Code */}
                        <td className="qr-code-cell">
                          {qrEnabledNames.some(name => 
                            link.first_name.toLowerCase().includes(name) || 
                            link.last_name.toLowerCase().includes(name)
                          ) && link.join_now_link ? (
                            <div 
                              className="qr-code-thumbnail"
                              onClick={() => {
                                setQrModalData({ 
                                  name: `${link.first_name} ${link.last_name}`, 
                                  link: link.join_now_link 
                                });
                                setQrModalOpen(true);
                              }}
                              title="Click to enlarge"
                            >
                              <QRCodeSVG 
                                value={link.join_now_link} 
                                size={40}
                                level="M"
                                bgColor="#ffffff"
                                fgColor="#000000"
                              />
                            </div>
                          ) : (
                            <span className="no-qr">-</span>
                          )}
                        </td>
                        
                        {/* Link Uses Left */}
                        <td className="uses-left-cell">
                          {editingId === link.id ? (
                            <select
                              value={editingData.uses_left !== undefined ? editingData.uses_left : 1}
                              onChange={(e) => handleEditInputChange('uses_left', parseInt(e.target.value))}
                              className="uses-left-select"
                            >
                              {Array.from({ length: 101 }, (_, i) => i).map(num => (
                                <option key={num} value={num}>{num}</option>
                              ))}
                            </select>
                          ) : (
                            <select
                              value={link.uses_left !== undefined ? link.uses_left : 1}
                              onChange={(e) => handleUsesLeftChange(link.id, e.target.value)}
                              className="uses-left-select"
                            >
                              {Array.from({ length: 101 }, (_, i) => i).map(num => (
                                <option key={num} value={num}>{num}</option>
                              ))}
                            </select>
                          )}
                        </td>
                        
                        {/* Actions */}
                        <td className="actions-cell">
                          {editingId === link.id ? (
                            <>
                              <Button
                                size="sm"
                                onClick={() => handleSaveEdit(link.id)}
                                className="action-btn save-btn"
                              >
                                <Check size={16} />
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={handleCancelEdit}
                                className="action-btn cancel-btn"
                              >
                                <X size={16} />
                              </Button>
                            </>
                          ) : (
                            <>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleEdit(link)}
                                className="action-btn edit-btn"
                              >
                                <Edit2 size={16} />
                              </Button>
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => handleDelete(link.id)}
                                className="action-btn delete-btn"
                              >
                                <Trash2 size={16} />
                              </Button>
                            </>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
        </>
        )}

        {/* QR Code Modal */}
        {qrModalOpen && (
          <div 
            className="qr-modal-overlay"
            onClick={() => setQrModalOpen(false)}
          >
            <div 
              className="qr-modal-content"
              onClick={(e) => e.stopPropagation()}
              ref={qrPrintRef}
            >
              <button 
                className="qr-modal-close"
                onClick={() => setQrModalOpen(false)}
              >
                <X size={24} />
              </button>
              <h3 className="qr-modal-title">{qrModalData.name}</h3>
              <div className="qr-modal-code">
                <QRCodeSVG 
                  value={qrModalData.link} 
                  size={280}
                  level="H"
                  bgColor="#ffffff"
                  fgColor="#000000"
                  includeMargin={true}
                />
              </div>
              <p className="qr-modal-link">{qrModalData.link}</p>
              <div className="qr-modal-actions">
                <Button 
                  onClick={() => {
                    navigator.clipboard.writeText(qrModalData.link);
                    toast.success('Link copied to clipboard!');
                  }}
                  className="qr-modal-btn"
                >
                  Copy Link
                </Button>
                <Button 
                  onClick={handlePrintQR}
                  className="qr-modal-btn qr-modal-print-btn"
                >
                  <Printer size={16} />
                  Print
                </Button>
                <Button 
                  onClick={handleSaveAsPDF}
                  className="qr-modal-btn qr-modal-pdf-btn"
                >
                  <FileDown size={16} />
                  Save PDF
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPanel;
